import { useState, useEffect, useRef } from 'react';
import HanziWriter from 'hanzi-writer';
import styles from './TourModal.module.css';

const BUDDY_NAMES = {
  ling: 'Ling',
  ming: 'Ming',
  feng: 'Feng',
  yong: 'Yong',
  meng: 'Meng',
  qi:   'Qi',
};

const STATE_COLOR  = { to_learn: '#e8634f', practicing: '#2ab8a8', known: '#766855' };
const STATE_BG     = { to_learn: 'rgba(255,104,33,0.18)', practicing: 'rgba(42,184,168,0.18)', known: 'transparent' };
const STATE_BORDER = { to_learn: '#e8634f', practicing: '#2ab8a8', known: '#211e1a' };
const STATE_LABEL  = { to_learn: 'NEW', practicing: 'PRACTICING', known: 'KNOWN' };

const LOOP_STATES    = ['to_learn', 'practicing', 'known'];
const LOOP_DURATIONS = { to_learn: 2500, practicing: 2500, known: 2000 };

export default function TourModal({ learnerName, buddyAvatar, onComplete, onSkip }) {
  const [slide, setSlide] = useState(1);

  const buddyName = BUDDY_NAMES[buddyAvatar] || 'your guide';
  const name = learnerName || 'your child';

  function handleNext() {
    if (slide < 4) setSlide(s => s + 1);
  }

  function handleComplete() {
    onComplete();
  }

  function handleSkip() {
    onSkip();
  }

  const topBgClass = {
    1: styles.topCream,
    2: styles.topOrange,
    3: styles.topCream,
    4: styles.topImage,
  }[slide];

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Welcome to WanderWords">
      <div className={styles.modal}>

        {/* Top half */}
        <div className={`${styles.topHalf} ${topBgClass}`}>
          {slide === 1 && <StateLoopAnimation />}
          {slide === 2 && (
            <img
              src={`/assets/onboarding/${buddyAvatar || 'ling'}.webp`}
              alt=""
              aria-hidden="true"
              className={styles.buddyImg}
            />
          )}
          {slide === 3 && <DailyLoopAnimation />}
          {slide === 4 && (
            <img
              src="/assets/tour-launch-hero.webp"
              alt=""
              aria-hidden="true"
              className={styles.launchHeroImg}
            />
          )}
        </div>

        {/* Bottom half */}
        <div className={styles.bottomHalf}>
          <div className={styles.copyBlock}>

            <div className={styles.headlineGroup}>
              <p className={styles.eyebrow}>
                {slide === 1 && 'THE LEARNING ENGINE'}
                {slide === 2 && 'OUR PROMISE'}
                {slide === 3 && 'DAILY LOOP'}
                {slide === 4 && 'OUR FIRST ADVENTURE'}
              </p>
              <h2 className={styles.headline}>
                {slide === 1 && `A system that learns as ${name} does.`}
                {slide === 2 && `${buddyName} is ready. So is ${name}.`}
                {slide === 3 && 'Read it. Write it. Own it.'}
                {slide === 4 && 'This is your story time, too.'}
              </h2>
            </div>

            <p className={styles.body}>
              {slide === 1 && `Most apps treat every child the same. WanderWords tracks every character ${name} encounters individually — each one moving through its own learning journey until it's truly known.`}
              {slide === 2 && `Stories create the kind of memory that drills never do. ${buddyName} will guide ${name} through adventures built around the characters they're learning — introducing new ones in context, repeating them naturally across stories, and making the time you spend together the anchor that makes it all stick.`}
              {slide === 3 && `New characters show pinyin — it fades as ${name} learns. Tap any character mid-story to explore its meaning, sound, and how to write it. In the Workbook, ${name} practises stroke-by-stroke — the way Mandarin is actually learned. Every story, review, and writing session earns XP.`}
              {slide === 4 && `WanderWords is made to be read together — you and ${name}, side by side. Your first story is ready. It's the beginning of something that lasts.`}
            </p>

            {slide === 1 && (
              <p className={styles.caption}>
                Every story is written exactly around where {name} is right now.
              </p>
            )}
          </div>

          <div className={slide < 4 ? styles.btnRow : styles.btnRowCta}>
            {slide < 4 ? (
              <>
                <button type="button" className={styles.btnSkip} onClick={handleSkip}>
                  Skip
                </button>
                <button type="button" className={styles.btnNext} onClick={handleNext}>
                  Next
                </button>
              </>
            ) : (
              <button type="button" className={styles.btnCta} onClick={handleComplete}>
                {/* TODO BP-TOUR-EVAL-01: replace navigate target with eval story once built */}
                Start your first adventure
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

function StateLoopAnimation() {
  const [currentState, setCurrentState] = useState('to_learn');

  useEffect(() => {
    let idx = 0;
    let timer;

    function cycle() {
      const state = LOOP_STATES[idx];
      setCurrentState(state);
      timer = setTimeout(() => {
        idx = (idx + 1) % LOOP_STATES.length;
        cycle();
      }, LOOP_DURATIONS[state] + 600);
    }

    cycle();
    return () => clearTimeout(timer);
  }, []);

  const pinyinOpacity = currentState === 'to_learn' ? 1 : currentState === 'practicing' ? 0.4 : 0;

  return (
    <div className={styles.stateLoopWrap}>
      <div
        className={styles.stateCharCard}
        style={{
          background: STATE_BG[currentState],
          borderColor: STATE_BORDER[currentState],
          transition: 'background 0.6s ease, border-color 0.6s ease',
        }}
      >
        <span
          className={styles.stateCharPinyin}
          style={{
            opacity: pinyinOpacity,
            transition: 'opacity 0.6s ease',
          }}
        >
          hǎo
        </span>
        <span className={styles.stateCharGlyph}>好</span>
      </div>

      <span className={styles.stateArrow} aria-hidden="true">→</span>

      <div className={styles.statePills}>
        {LOOP_STATES.map((s) => {
          const isActive = s === currentState;
          return (
            <div
              key={s}
              className={styles.statePill}
              style={{
                background:   isActive ? STATE_BG[s]     : 'transparent',
                borderColor:  STATE_BORDER[s],
                color:        isActive ? STATE_COLOR[s]  : STATE_BORDER[s],
                opacity:      isActive ? 1 : 0.45,
                transform:    isActive ? 'scale(1.04)' : 'scale(1)',
                transition:   'background 0.6s ease, color 0.6s ease, opacity 0.4s ease, transform 0.4s ease',
              }}
            >
              {STATE_LABEL[s]}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const STORY_CHARS = [
  { char: '今', state: 'known',    pinyin: 'jīn'  },
  { char: '天', state: 'known',    pinyin: 'tiān' },
  { char: '天', state: 'known',    pinyin: 'tiān' },
  { char: '气', state: 'known',    pinyin: 'qì'   },
  { char: '好', state: 'to_learn', pinyin: 'hǎo', isTarget: true },
  { char: '。', state: null,       pinyin: null   },
];

function DailyLoopAnimation() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pulse, setPulse]           = useState(false);
  const [practicing, setPracticing] = useState(false);
  const writerContainerRef          = useRef(null);
  const writerRef                   = useRef(null);
  const timersRef                   = useRef([]);

  function addTimer(fn, delay) {
    const t = setTimeout(fn, delay);
    timersRef.current.push(t);
    return t;
  }

  function clearAllTimers() {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }

  function destroyWriter() {
    if (writerContainerRef.current) writerContainerRef.current.innerHTML = '';
    writerRef.current = null;
  }

  function startWriter() {
    destroyWriter();
    const container = writerContainerRef.current;
    if (!container) return;

    const writer = HanziWriter.create(container, '好', {
      width: 140,
      height: 140,
      padding: 8,
      showOutline: true,
      showCharacter: false,
      strokeAnimationSpeed: 0.8,
      delayBetweenStrokes: 200,
      strokeColor: '#211e1a',
      outlineColor: '#b5ada0',
      highlightColor: '#ff6821',
    });

    // NOTE: I-VIEWBOX-01 patch is intentionally NOT applied here.
    // The writerContainer CSS is exactly 140×140 — matching the writer
    // dimensions. No CSS scaling occurs so the patch is not needed.
    // The patch only applies where CSS scales the container below the
    // SVG's native size (GameplayScreen, CharacterProfileContent).

    writerRef.current = writer;

    writer.animateCharacter({
      onComplete: () => {
        setDrawerOpen(false);
        destroyWriter();
        addTimer(() => setPracticing(true), 440);
        addTimer(() => {
          clearAllTimers();
          setPracticing(false);
          addTimer(() => startSequence(), 200);
        }, 1840);
      },
    });
  }

  function startSequence() {
    addTimer(() => setPulse(true),                              1800);
    addTimer(() => { setPulse(false); setDrawerOpen(true); },  2200);
    addTimer(() => startWriter(),                               2650);
  }

  useEffect(() => {
    startSequence();
    return () => {
      clearAllTimers();
      destroyWriter();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const targetColor = practicing ? STATE_COLOR.practicing : STATE_COLOR.to_learn;
  const targetBg    = practicing ? STATE_BG.practicing    : STATE_BG.to_learn;

  return (
    <div className={styles.dailyLoopWrap}>

      {/* Story strip — left side */}
      <div className={styles.storyStrip}>
        {STORY_CHARS.map((c, i) => {
          if (!c.state) return (
            <span key={i} className={styles.stripPunct}>{c.char}</span>
          );
          const isTarget = c.isTarget;
          return (
            <span
              key={i}
              className={`${styles.stripChar} ${isTarget ? styles.stripCharTarget : ''} ${isTarget && pulse ? styles.stripCharPulse : ''}`}
              style={isTarget ? { background: targetBg, transition: 'background 0.6s ease' } : {}}
            >
              <span
                className={styles.stripPinyin}
                style={{ visibility: isTarget ? 'visible' : 'hidden' }}
              >
                {c.pinyin}
              </span>
              <span
                className={styles.stripGlyph}
                style={isTarget ? { color: targetColor, transition: 'color 0.6s ease' } : {}}
              >
                {c.char}
              </span>
            </span>
          );
        })}
      </div>

      {/* Right-side stroke drawer */}
      <div className={`${styles.rightDrawer} ${drawerOpen ? styles.rightDrawerOpen : ''}`}>
        <div ref={writerContainerRef} className={styles.writerContainer} />
        <p className={styles.drawerPinyin}>hǎo</p>
        <p className={styles.drawerMeaning}>good · well</p>
      </div>

    </div>
  );
}
